import { ApolloClient, HttpLink, InMemoryCache, split } from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import { GraphQLWsLink } from "@apollo/client/link/subscriptions"
import { getMainDefinition } from "@apollo/client/utilities"
import { IdToken } from "@auth0/auth0-spa-js"
import { createClient } from "graphql-ws"

const GRAPH_API_URL = process.env["REACT_APP_GRAPH_API_URL"]
const GRAPH_API_WS_URL = process.env["REACT_APP_GRAPH_API_WS_URL"]

enum SiteType {
    DEMO = "DEMO",
    PRODUCTION = "PRODUCTION",
}

interface AuthenticatedSite {
    name: string
    imgUri?: string
    siteId: string
    verified?: boolean
    userTypes?: string[]
    type: SiteType
}

if (!GRAPH_API_URL) {
    throw new Error("GRAPH_API_URL is not set")
}
if (!GRAPH_API_WS_URL) {
    throw new Error("GRAPH_API_WS_URL is not set")
}

const httpLink = new HttpLink({
    uri: GRAPH_API_URL,
})

// Function to check if the access token has expired
export const isTokenExpired = (expiresAt: string | number | null): boolean => {
    if (!expiresAt) {
        return true
    }

    const expirationTime = Number(expiresAt)
    const currentTime = Math.floor(new Date().getTime() / 1000)
    return expirationTime < currentTime
}

export const createApolloClientWithToken = async (accessToken: string) => {
    const wsLink =
        typeof window !== "undefined"
            ? new GraphQLWsLink(
                  createClient({
                      url: GRAPH_API_WS_URL,
                      connectionParams: async () => {
                          const selectedSite: AuthenticatedSite = JSON.parse(
                              window.sessionStorage.getItem("selectedSite") || "{}"
                          )
                          const params = { Authorization: accessToken } as any
                          if (selectedSite?.siteId) {
                              params["x-apollo-tenant-id"] = encodeURIComponent(selectedSite.siteId)

                              const shiftId = window.sessionStorage.getItem("shiftId")
                              const vehicleId = window.sessionStorage.getItem("vehicleId")

                              if (shiftId && vehicleId) {
                                  params["vehicleId"] = vehicleId
                              }
                          }

                          return params
                      },
                  })
              )
            : null

    const splitLink =
        typeof window !== "undefined" && wsLink != null
            ? split(
                  ({ query }) => {
                      const definition = getMainDefinition(query)
                      return definition.kind === "OperationDefinition" && definition.operation === "subscription"
                  },
                  wsLink,
                  httpLink
              )
            : httpLink

    const operationsWithoutSite = [
        "MyUser",
        "UpdateMyUser",
        "MyUserSubscription",
        "ActionUserInvitation",
        "CreateDocument",
    ]

    const apolloAuthContext = setContext(async (requestDetails, { headers }) => {
        const selectedSite: AuthenticatedSite = JSON.parse(window.sessionStorage.getItem("selectedSite") || "{}")
        if (
            !selectedSite?.siteId &&
            (!requestDetails.operationName || !operationsWithoutSite.includes(requestDetails.operationName))
        ) {
            throw new Error(`Required permission missing for operation: ${requestDetails.operationName}`) // Throw an error if no site is selected
        }

        const accessTokenExpiresAt = window.sessionStorage.getItem("accessTokenExpiresAt") || ""
        if (accessTokenExpiresAt !== "" && isTokenExpired(accessTokenExpiresAt)) {
            throw new Error("Session has been Expired! Please login again")
        }

        const requestHeaders = {
            ...headers,
            Authorization: await accessToken,
            "x-forwarded-proto": "https",
        }

        if (selectedSite?.siteId) {
            requestHeaders["x-apollo-tenant-id"] = encodeURIComponent(selectedSite?.siteId)
        }

        return {
            headers: requestHeaders,
        }
    })

    //Avoid network call & Redirect to login page if apolloAuthContext throw an error
    if (!apolloAuthContext) {
        console.log("Token Expired! Redirecting to Login...")
    }

    const client = new ApolloClient({
        link: typeof window !== "undefined" ? apolloAuthContext.concat(splitLink) : undefined,
        cache: new InMemoryCache(),
    })

    return client
}

export const createApolloClient = async (getAccessTokenSilently: () => Promise<IdToken | undefined>) => {
    const response = await getAccessTokenSilently()
    const idToken = response?.__raw || ""

    return createApolloClientWithToken(idToken)
}
